import { Spinner } from '@Common/Spinner';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { API } from 'api';
import { defaultToastProps } from 'appConstants';
import { FormEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { useGetPaymentsList } from 'store/subscription/actions';

interface StripeAdditionalFormProps {
  closeModal: () => void;
}

export const StripeAdditionalForm = ({ closeModal }: StripeAdditionalFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { refetch } = useGetPaymentsList();

  const [loading, setLoading] = useState(false);

  const handleError = (error: StripeError) => {
    setLoading(false);
    toast(error.message, {
      ...defaultToastProps,
      type: 'error',
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    // Create the SetupIntent and obtain clientSecret
    const { data } = await API.v1.subscription.getClientSecret();

    // Confirm the SetupIntent using the details collected by the Payment Element
    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret: data.client_secret,
      confirmParams: {
        return_url: window.location.origin + '/subscription',
      },
      redirect: 'if_required',
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // confirming the setup. Show the error to your customer (for example, payment details incomplete)
      handleError(error);
    } else {
      toast('Payment method successfully added', {
        ...defaultToastProps,
        type: 'success',
      });
      refetch();
      setLoading(false);
      closeModal();
      // Your customer is redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer is redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <div className="w-full flex justify-end">
        <button
          disabled={!stripe || loading}
          type="submit"
          className="mt-3 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          {loading ? <Spinner color="bg-white" /> : <FormattedMessage id="submit" />}
        </button>
      </div>
    </form>
  );
};
